import type { Theme } from '@material-ui/core'
import { makeStyles, Typography, useTheme } from '@material-ui/core'
import classnames from 'classnames'
import React, { useEffect, useState } from 'react'
import Skeleton from '@ui/core/Skeleton'

import type { WithUseStyles } from '../styles'
import Table, { TableBody, TableCell, TableHead, TableRow } from '../Table'
import TablePagination from './TablePagination'

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  table: {},
  tableBody: {},
  tableHead: {
    '& $div': {
      borderBottom: '0px solid transparent'
    }
  },
  noResults: {
    height: 200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.disabled
  },
  shimCell: {
    padding: 16
  },
  div: {}
}))

export interface ResultsTableProps<T = any> {
  className?: string
  rows: T[]

  /**
   * Renders the element for the row
   */
  renderRow: (row: T, index: number) => JSX.Element

  shimHeight?: string

  /**
   * A <TableRow> component to render into <TableHead>
   *
   */
  columns: JSX.Element

  /**
   * The current page
   */
  page: number

  /**
   * Total pages of data available
   */
  pages?: number

  /**
   * Number of rows to show per page
   */
  rowsPerPage: number

  /**
   * If true, the rows will be shimmed
   */
  loading?: boolean

  /**
   * Shown when there is no data
   */
  noResultsMessage?: React.ReactNode

  /**
   * Disables pagination and hides the footer
   */
  disablePagination?: boolean

  /**
   * Called when the page is changed by the user
   */
  onPageChange?: (page: number) => any
}

/**
 * A table for showing the results of a query or search with pagination
 */
function ResultsTable({
  rows,
  renderRow,
  rowsPerPage,
  page,
  pages,
  columns,
  loading,
  noResultsMessage,
  disablePagination,
  onPageChange,
  className,
  shimHeight = '16px',
  classes: propClasses
}: ResultsTableProps & WithUseStyles<typeof useStyles>) {
  const classes = useStyles({ classes: propClasses })
  const theme = useTheme<Theme>()

  const [currentPage, setCurrentPage] = useState(page)

  const rowsForPage = getRowsForPage({
    rows,
    rowsPerPage,
    page: currentPage - 1
  })
  const isFirstPage = currentPage <= 1
  const isLastPage = currentPage >= pages || !pages || pages === 1

  function handleChangePage(event, nextPage) {
    if (onPageChange) {
      onPageChange(nextPage)
    }

    setCurrentPage(nextPage)
  }

  useEffect(() => {
    setCurrentPage(page)
  }, [page])

  const noResults = rowsForPage.length === 0 && !loading
  const pageHasRows = rowsForPage.length > 0

  return (
    <div className={classnames(className, classes.root)}>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>{columns}</TableHead>
        {pageHasRows && <TableBody className={classes.tableBody}>{rowsForPage.map(renderRow)}</TableBody>}
      </Table>
      {/* because our table is made of divs, this is the best way to have a single cell span the entire row*/}
      {loading && !pageHasRows && (
        <Table data-testid={'loading-table'} className={classes.table}>
          <TableBody className={classes.tableBody}>
            {Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={index}>
                <TableCell className={classes.shimCell}>
                  <Skeleton width="100%" height={shimHeight} color={theme.palette.grey[200]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {noResults && (
        <div className={classes.noResults}>
          <Typography variant="h6" align="center">
            {noResultsMessage}
          </Typography>
        </div>
      )}
      {!disablePagination && !noResults && (
        <TablePagination
          currentPage={currentPage}
          pages={pages}
          disablePrevPage={loading || isFirstPage}
          disableNextPage={loading || isLastPage}
          disableFirstPage={loading || isFirstPage}
          disableLastPage={loading || isLastPage}
          onChangePage={handleChangePage}
        />
      )}
    </div>
  )
}

function getRowsForPage(args: { rows: any[]; page: number; rowsPerPage: number }) {
  const { rows, page, rowsPerPage } = args
  return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
}

ResultsTable.defaultProps = {
  page: 1,
  noResultsMessage: 'No results',
  selected: []
}

export default ResultsTable
